import { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Users, 
  Cpu, 
  Database,
  TrendingUp,
  Plus,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { formatRelativeTime, formatTokens, truncate } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Session, Message } from '@/types';

interface Stats {
  totalSessions: number;
  totalMessages: number;
  totalTokens: number;
  activePlatforms: number;
}

export function Dashboard() {
  const [stats, setStats] = useState<Stats>({
    totalSessions: 0,
    totalMessages: 0,
    totalTokens: 0,
    activePlatforms: 0,
  });
  const [recentSessions, setRecentSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sessionsRes] = await Promise.all([
        apiClient.listSessions({ limit: 10 }),
      ]);
      
      const sessions = sessionsRes.sessions || [];
      setRecentSessions(sessions);
      
      // Calculate stats
      const totalSessions = sessionsRes.total || sessions.length;
      const totalMessages = sessions.reduce((sum, s) => sum + s.message_count, 0);
      const totalTokens = sessions.reduce((sum, s) => sum + s.total_tokens, 0);
      const platforms = new Set(sessions.map(s => s.platform).filter(Boolean)).size;
      
      setStats({
        totalSessions,
        totalMessages,
        totalTokens,
        activePlatforms: platforms,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Sessions', value: stats.totalSessions, icon: MessageSquare, color: 'blue' },
    { label: 'Total Messages', value: stats.totalMessages, icon: Users, color: 'green' },
    { label: 'Total Tokens', value: formatTokens(stats.totalTokens), icon: Cpu, color: 'purple' },
    { label: 'Active Platforms', value: stats.activePlatforms, icon: Database, color: 'orange' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of your Anika Agent activity</p>
        </div>
        <Button onClick={loadData} loading={loading}>
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-xl bg-${stat.color}-100 dark:bg-${stat.color}-900/30`}>
                <stat.icon className={`h-6 w-6 text-${stat.color}-600 dark:text-${stat.color}-400`} />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-gray-500 dark:text-gray-400">
              <TrendingUp className="h-3 w-3 mr-1" />
              <span>Updated {formatRelativeTime(Date.now())}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Sessions */}
      <div className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700">
        <div className="p-6 border-b border-gray-200 dark:border-dark-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Sessions</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/sessions'}>
              View All
            </Button>
            <Button size="sm" onClick={() => window.location.href = '/chat'}>
              <Plus className="h-4 w-4 mr-1" />
              New Chat
            </Button>
          </div>
        </div>
        
        {error && (
          <div className="p-6 text-center text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="divide-y divide-gray-200 dark:divide-dark-700">
          {recentSessions.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No sessions yet</p>
              <p className="text-sm mt-1">Start a conversation from the Chat page or connect a platform</p>
              <Button className="mt-4" onClick={() => window.location.href = '/chat'}>
                Start New Chat
              </Button>
            </div>
          ) : (
            recentSessions.map((session) => (
              <div key={session.id} className="p-6 hover:bg-gray-50 dark:hover:bg-dark-800/50 cursor-pointer transition-colors"
                onClick={() => window.location.href = `/chat/${session.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                      <MessageSquare className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {session.display_name || session.session_key}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {session.platform} • {session.message_count} messages • {formatTokens(session.total_tokens)} tokens
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatRelativeTime(session.last_active)}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {session.model || 'No model'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}